/**
 * Vue Composable: useTheme()
 *
 * Verwaltet das ID-basierte Theme-System
 * - Lädt Themes aus themes.json
 * - ID-Format: 0xxxxx (Light), 1xxxxx (Dark)
 * - Setzt CSS-Variablen auf document.documentElement
 * - Persistiert in localStorage
 */

import { ref, computed } from 'vue'
import { API_BASE_URL } from '@/config'

export interface ThemeTokens {
  primary: string
  secondary: string
  tertiary: string
  accent: string
  text: string
}

export interface Theme {
  id: string
  mode: 'light' | 'dark'
  label: string
  tokens: ThemeTokens
}

interface ThemesConfig {
  defaultThemeId: string
  themes: Theme[]
}

/** Eingebauter Fallback — wird verwendet wenn themes.json nicht geladen werden kann. */
const FALLBACK_DEFAULT_THEME_ID = '000001'

const FALLBACK_THEMES: Theme[] = [
  {
    id: '000001',
    mode: 'light',
    label: 'Default Light',
    tokens: {
      primary: '#FAFAFA',
      secondary: '#F0F3FA',
      tertiary: '#E5E7EB',
      accent: '#E4003A',
      text: '#000000',
    },
  },
  {
    id: '100001',
    mode: 'dark',
    label: 'Default Dark',
    tokens: {
      primary: '#1A1A1A',
      secondary: '#383838',
      tertiary: '#371B1B',
      accent: '#E4003A',
      text: '#FFFFFF',
    },
  },
]

/** Dynamisch geladene oder gefallte Themes */
let loadedThemesConfig: ThemesConfig | null = null
let allThemesList: Theme[] = []
let isThemesLoaded = false

/** Lade themes.json dynamisch mit eingebautem Fallback */
async function fetchThemes(): Promise<ThemesConfig> {
  // Prüfe zuerst ob bereits geladen
  if (loadedThemesConfig) return loadedThemesConfig

  try {
    const response = await fetch(new URL('@/assets/themes.json', import.meta.url))
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data: ThemesConfig = await response.json()
    loadedThemesConfig = data
    console.log(`[useTheme] themes.json geladen (${data.themes.length} Themes)`)
    return data
  } catch (err) {
    console.warn(`[useTheme] themes.json konnte nicht geladen werden, nutze Fallback-Themes:`, err)
    // Fallback: Bau eine ThemesConfig aus den eingebauten Default-Themes
    loadedThemesConfig = {
      defaultThemeId: FALLBACK_DEFAULT_THEME_ID,
      themes: FALLBACK_THEMES,
    }
    return loadedThemesConfig
  }
}

/** Initialisiere die Themes dynamisch (async) */
async function initThemes(): Promise<void> {
  if (isThemesLoaded) return

  const config = await fetchThemes()
  allThemesList = config.themes
  // Auch den reaktiven Ref aktualisieren für TopBar.vue
  currentThemesRef.value = [...allThemesList]
  validateThemeIDPrefixes(allThemesList)
  isThemesLoaded = true
}

/** Initialisiere die Themes einmalig aus dem Fallback (sync-Fall für nicht-async-Kontexte) */
function initFallbackThemes(): void {
  if (isThemesLoaded) return
  allThemesList = FALLBACK_THEMES
  loadedThemesConfig = { defaultThemeId: FALLBACK_DEFAULT_THEME_ID, themes: FALLBACK_THEMES }
  currentThemesRef.value = [...FALLBACK_THEMES] // Auch den reaktiven Ref aktualisieren
  currentThemeIdRef.value = FALLBACK_DEFAULT_THEME_ID
  isThemesLoaded = true
}

/**
 * Validiert dass Light-IDs mit '0' und Dark-IDs mit '1' beginnen
 * Warnt bei jeder Inkonsistenz zwischen ID-Prefix und Mode-Feld
 */
function validateThemeIDPrefixes(themes: Theme[]): void {
  const violations: string[] = []

  for (const theme of themes) {
    const prefix = theme.id.charAt(0)
    const expectedMode = prefix === '1' ? 'dark' : 'light'

    if (theme.mode !== expectedMode) {
      violations.push(
        `[${theme.id}] mode="${theme.mode}" aber Prefix="${prefix}" → erwarteter Mode="${expectedMode}"`
      )
    }
  }

  // defaultThemeId auch prüfen
  const themesById = new Map(themes.map((t) => [t.id, t]))
  const defaultId = loadedThemesConfig?.defaultThemeId || FALLBACK_DEFAULT_THEME_ID
  if (defaultId) {
    const defTheme = themesById.get(defaultId)
    if (defTheme) {
      const prefix = defTheme.id.charAt(0)
      const expectedMode = prefix === '1' ? 'dark' : 'light'
      if (defTheme.mode !== expectedMode) {
        violations.push(
          `defaultThemeId="${defTheme.id}" mode="${defTheme.mode}" aber Prefix="${prefix}" → erwarteter Mode="${expectedMode}"`
        )
      }
    }
  }

  if (violations.length > 0) {
    console.error(
      '[useTheme] Theme ID / Mode mismatch detected:',
      violations.length,
      'violation(s):'
    )
    violations.forEach((v) => console.error('  -', v))
    // throws to break the app — a wrong prefix would silently select the wrong mode everywhere
    throw new Error(
      `[useTheme] ${violations.length} theme ID prefix violation(s) — light themes must start with 0, dark themes with 1.\n` +
        violations.map((v) => `  ${v}`).join('\n')
    )
  } else {
    console.log(
      `[useTheme] OK: All ${themes.length} theme IDs match their mode prefixes (light=0, dark=1).`
    )
  }
}

/**
 * Validiert Theme-ID Format (6-stellig, 0 oder 1 am Anfang)
 */
function isValidThemeId(id: string): boolean {
  return /^[01]\d{5}$/.test(id)
}

/**
 * Setzt CSS-Variablen auf HTML-Element
 */
function setCSSVariables(theme: Theme): void {
  const root = document.documentElement
  const tokens = theme.tokens

  root.style.setProperty('--color-primary', tokens.primary)
  root.style.setProperty('--color-secondary', tokens.secondary)
  root.style.setProperty('--color-tertiary', tokens.tertiary)
  root.style.setProperty('--color-accent', tokens.accent)
  root.style.setProperty('--color-text', tokens.text)

  // --color-text-main wird von Tailwind @theme und Komponenten referenziert
  root.style.setProperty('--color-text-main', tokens.text)

  // Setze data-Attribute
  root.setAttribute('data-mode', theme.mode)
  root.setAttribute('data-theme-id', theme.id)
}

/**
 * Lade die gespeicherten Theme-Einstellungen vom Backend
 */
async function loadThemeFromBackend(): Promise<{
  currentColourScheme: string
  colourSchemeLight: string
  colourSchemeDark: string
  fontSize: number
} | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/users/settings`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      console.warn('[useTheme] Could not load theme from backend:', response.statusText)
      return null
    }

    const data = await response.json()
    return {
      currentColourScheme: data.current_colour_scheme,
      colourSchemeLight: data.colour_scheme_light,
      colourSchemeDark: data.colour_scheme_dark,
      fontSize: data.font_size,
    }
  } catch (error) {
    console.warn('[useTheme] Error loading theme from backend:', error)
    return null
  }
}

/**
 * Speichere Theme-Einstellungen im Backend
 */
async function saveThemeToBackend(settings: {
  current_colour_scheme?: string
  colour_scheme_light?: string
  colour_scheme_dark?: string
}): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/users/settings`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    })

    if (!response.ok) {
      console.warn('[useTheme] Could not save theme to backend:', response.statusText)
      return false
    }

    console.log('[useTheme] Theme settings saved to backend')
    return true
  } catch (error) {
    console.warn('[useTheme] Error saving theme to backend:', error)
    return false
  }
}

export function useTheme() {
  const currentThemeId = ref<string>('000001')
  const allThemes = ref<Theme[]>([])
  const isLoaded = ref(false)

  /**
   * Initialisiere Theme-System (async mit dynamischem Laden)
   */
  async function init(): Promise<void> {
    if (isLoaded.value) return

    // Dynamisch Themes laden (mit Fallback)
    await initThemes()

    allThemes.value = [...allThemesList]
    isLoaded.value = true

    // Priorität: per-mode localStorage keys (selected-dark/light-theme-id) — legacy themeId zuletzt
    let savedThemeId = ''

    const darkModeId = localStorage.getItem('selected-dark-theme-id')
    const lightModeId = localStorage.getItem('selected-light-theme-id')

    // Wenn eine per-mode ID existiert, nutze sie (Dark hat Priorität: letzter aktiver Mode gewinnt)
    if (darkModeId && isValidThemeId(darkModeId)) {
      savedThemeId = darkModeId
    } else if (lightModeId && isValidThemeId(lightModeId)) {
      savedThemeId = lightModeId
    }

    // Legacy fallback — wird bald entfernt
    if (!savedThemeId) {
      const legacy = localStorage.getItem('themeId')
      if (legacy && isValidThemeId(legacy)) savedThemeId = legacy
    }

    // Fallback: nur wenn themes.json NICHT geladen werden konnte → ID 1 ('000001')
    // Ansonsten: defaultThemeId aus themes.json nutzen
    if (!savedThemeId || !isValidThemeId(savedThemeId)) {
      const configDefault = loadedThemesConfig?.defaultThemeId
      if (isThemesLoaded && loadedThemesConfig && isValidThemeId(configDefault || '')) {
        // themes.json wurde geladen und hat eine gültige defaultThemeId → nutze diese
        savedThemeId = configDefault!
      } else {
        // themes.json konnte nicht geladen werden oder hat keine gültige defaultThemeId
        savedThemeId = FALLBACK_DEFAULT_THEME_ID
      }
    }

    currentThemeId.value = savedThemeId
    // Synchronisiere auch den externen Ref für Slider/TopBar
    currentThemeIdRef.value = savedThemeId
    applyTheme(currentThemeId.value)

    // Höre auf localStorage-Änderungen (z.B. von themes-showcase.html)
    setupStorageListener()

    // Höre auf custom themeChanged events
    setupThemeChangeListener()
  }

  /**
   * Gibt Theme nach ID zurück
   */
  function getThemeById(id: string): Theme | undefined {
    return allThemes.value.find((t) => t.id === id)
  }

  /**
   * Gibt aktuelles Theme zurück
   */
  const currentTheme = computed((): Theme | undefined => {
    return getThemeById(currentThemeId.value || '')
  })

  /**
   * Gibt nur Light-Themes zurück
   */
  const lightThemes = computed((): Theme[] => {
    return allThemes.value.filter((t) => t.mode === 'light')
  })

  /**
   * Gibt nur Dark-Themes zurück
   */
  const darkThemes = computed((): Theme[] => {
    return allThemes.value.filter((t) => t.mode === 'dark')
  })

  /**
   * Wende Theme an (CSS + localStorage)
   */
  function applyTheme(themeId: string): boolean {
    if (!isValidThemeId(themeId)) {
      console.warn(`[useTheme] Invalid theme ID: ${themeId}`)
      return false
    }

    // Stelle sicher dass Themes geladen sind (falls applyTheme vor init() aufgerufen wird)
    if (!isLoaded.value) {
      allThemes.value = [...allThemesList]
      isLoaded.value = true
    }

    const theme = getThemeById(themeId)
    if (!theme) {
      console.warn(`[useTheme] Theme not found: ${themeId}`)
      return false
    }

    currentThemeId.value = themeId
    currentThemeIdRef.value = themeId
    setCSSVariables(theme)

    // Speichere in localStorage
    try {
      localStorage.setItem('themeId', themeId)
    } catch (error) {
      console.warn('[useTheme] Could not save theme to localStorage:', error)
    }

    return true
  }

  /**
   * Lade Theme aus Profil (z.B. nach Login)
   */
  async function loadFromProfile(profileThemeId: string): Promise<void> {
    if (!isLoaded.value) {
      await init()
    }

    if (profileThemeId && isValidThemeId(profileThemeId)) {
      applyTheme(profileThemeId)
    } else {
      console.warn('[useTheme] Invalid profile theme ID')
    }
  }

  /**
   * Setze Theme nach ID
   */
  async function setThemeById(themeId: string): Promise<boolean> {
    if (!isLoaded.value) {
      await init()
    }

    return applyTheme(themeId)
  }

  /**
   * Setze Theme nach ID und speichere auch im Backend (für ViewModeController)
   * Diese Funktion wird verwendet, um das Theme zu speichern und auch die Legacy-IDE-Einstellungen zu aktualisieren
   * WICHTIG: Speichert Light und Dark Themes getrennt!
   */
  async function setThemeByIdWithPersist(themeId: string): Promise<boolean> {
    // Wende das Theme an (setzt auch localStorage)
    const success = await setThemeById(themeId)

    if (!success) {
      return false
    }

    // Konvertiere ID zu Theme-Namen für die Legacy IDE
    // Light themes: 0xxxxx -> "light", Dark themes: 1xxxxx -> "dark"
    const themeName = themeId.startsWith('1') ? 'dark' : 'light'
    const isLight = themeName === 'light'

    // Speichere separat für Light und Dark Mode
    if (isLight) {
      localStorage.setItem('selected-light-theme-id', themeId)
      // Speichere Light-Theme-ID im Backend
      await saveThemeToBackend({
        current_colour_scheme: 'light',
        colour_scheme_light: themeId,
        colour_scheme_dark: localStorage.getItem('selected-dark-theme-id') || '100001',
      })
      localStorage.removeItem('themeId')
    } else {
      localStorage.setItem('selected-dark-theme-id', themeId)
      // Speichere Dark-Theme-ID im Backend
      await saveThemeToBackend({
        current_colour_scheme: 'dark',
        colour_scheme_light: localStorage.getItem('selected-light-theme-id') || '000001',
        colour_scheme_dark: themeId,
      })
      localStorage.removeItem('themeId')
    }

    // Aktualisiere auch die Backend-Einstellungen über die Legacy IDE
    // Rufe ViewModeController mit beiden Modes auf
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const window_obj = window as any
      if (window_obj.main && window_obj.main.viewModeController) {
        // Speichere für BEIDE Modes (nicht nur den aktuellen!)
        const settings = window_obj.main.user.settings
        if (settings && settings.viewModes) {
          // Aktualisiere den Mode, dessen Theme wir gerade setzen
          if (isLight) {
            // Presentation Mode = Light
            if (settings.viewModes.presentation) {
              settings.viewModes.presentation.theme = themeName
            }
          } else {
            // Monitor Mode = Dark
            if (settings.viewModes.monitor) {
              settings.viewModes.monitor.theme = themeName
            }
          }
        }

        // Triggere Speicherung
        window_obj.main.viewModeController.saveSettings()
        console.log(
          '[useTheme] Theme persisted to backend:',
          themeName,
          'for',
          isLight ? 'light' : 'dark'
        )
      }
    } catch (error) {
      console.warn('[useTheme] Could not persist theme to backend:', error)
    }

    return true
  }

  /**
   * Gebe Default-Theme-ID zurück
   */
  function getDefaultThemeId(): string {
    return loadedThemesConfig?.defaultThemeId || FALLBACK_DEFAULT_THEME_ID
  }

  /**
   * Lade Themes synchron (nutze nur wenn async nicht möglich ist)
   */
  function loadSyncThemes(): void {
    if (!isLoaded.value) {
      // Async versuchen
      initThemes()
        .then(() => {
          allThemes.value = [...allThemesList]
          isLoaded.value = true
        })
        .catch(() => {
          // Bei Fehler: Fallback laden
          initFallbackThemes()
          allThemes.value = [...FALLBACK_THEMES]
          isLoaded.value = true
          const firstLight = FALLBACK_THEMES.find((t) => t.mode === 'light')
          if (firstLight && isValidThemeId(firstLight.id)) {
            currentThemeId.value = firstLight.id
            applyTheme(firstLight.id)
          }
        })
    }
  }

  /**
   * Prüfe auf Theme-ID Prefix Inkonsistenzen
   */

  /**
   * Höre auf localStorage-Änderungen von anderen Tabs oder der themes-showcase.html
   */
  function setupStorageListener(): void {
    if (typeof window === 'undefined') return

    window.addEventListener('storage', async (event: StorageEvent) => {
      // Höre auf die neuen separaten Keys für Light und Dark Themes
      if (
        (event.key === 'selected-light-theme-id' || event.key === 'selected-dark-theme-id') &&
        event.newValue &&
        isValidThemeId(event.newValue)
      ) {
        // Wende das Theme an und speichere auch im Backend
        void setThemeByIdWithPersist(event.newValue)
        console.log('[useTheme] Theme updated from storage:', event.key, '=', event.newValue)
      }
    })
  }

  /**
   * Höre auf custom 'themeChanged' events von themes-showcase.html
   */
  function setupThemeChangeListener(): void {
    if (typeof window === 'undefined') return

    window.addEventListener('themeChanged', (event: Event) => {
      const customEvent = event as CustomEvent
      if (customEvent.detail && isValidThemeId(customEvent.detail.themeId)) {
        // Wende das Theme an und speichere auch im Backend
        void setThemeByIdWithPersist(customEvent.detail.themeId)
        console.log('[useTheme] Theme updated from event:', customEvent.detail.themeId)
      }
    })
  }

  return {
    // State
    currentThemeId,
    currentTheme,
    allThemes,
    lightThemes,
    darkThemes,
    isLoaded,

    // Methods
    init,
    setThemeById,
    setThemeByIdWithPersist,
    loadFromProfile,
    getThemeById,
    getDefaultThemeId,
    loadSyncThemes,
  }
}

/**
 * Setzt den Theme-Mode und synchronisiert alle Consumers (Slider, CSS, localStorage).
 * Priorisiert per-mode localStorage keys — beim Toggle wird die gespeicherte Theme-ID
 * für den neuen Modus beibehalten statt willkürlich ein anderes Theme zu wählen.
 */
export function setActiveThemeMode(mode: 'light' | 'dark'): void {
  // 1. Gespeicherte ID für diesen Modus priorisieren (User's choice erhalten)
  const key = mode === 'dark' ? 'selected-dark-theme-id' : 'selected-light-theme-id'
  let themeId = localStorage.getItem(key)

  // 2. Fallback: beliebige Theme-ID für den Modus aus den geladenen Themes
  if (!themeId || !/^[01]\d{5}$/.test(themeId)) {
    const fallbackIdx = mode === 'light' ? 0 : 3
    themeId =
      allThemesList.find((t) => t.mode === mode && /^[01]\d{5}$/.test(t.id))?.id ??
      FALLBACK_THEMES[fallbackIdx].id
  }

  // 3. Tokens holen (falls Themes noch nicht geladen: Fallback-Tokens)
  const matchedTheme = allThemesList.find((t) => t.id === themeId)
  let tokens = matchedTheme?.tokens
  if (!tokens) {
    const fallbackIdx = mode === 'light' ? 0 : 3
    tokens = FALLBACK_THEMES[fallbackIdx].tokens
  }

  // 4. CSS-Variablen setzen (TopBar-Farben usw.)
  const root = document.documentElement
  root.style.setProperty('--color-primary', tokens.primary)
  root.style.setProperty('--color-secondary', tokens.secondary)
  root.style.setProperty('--color-tertiary', tokens.tertiary)
  root.style.setProperty('--color-accent', tokens.accent)
  root.style.setProperty('--color-text', tokens.text)
  root.style.setProperty('--color-text-main', tokens.text)

  root.setAttribute('data-mode', mode)
  root.setAttribute('data-theme-id', themeId)

  if (mode === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }

  // 5. Per-mode + localStorage synchron halten
  localStorage.setItem(key, themeId)
  localStorage.setItem('theme', mode)

  // Speichere Theme-Einstellungen im Backend (async, aber nicht awaiten um UI nicht zu blockieren)
  const themeName = mode
  const isLight = mode === 'light'
  saveThemeToBackend({
    current_colour_scheme: themeName,
    colour_scheme_light: isLight
      ? themeId
      : localStorage.getItem('selected-light-theme-id') || '000001',
    colour_scheme_dark: !isLight
      ? themeId
      : localStorage.getItem('selected-dark-theme-id') || '100001',
  }).catch((error) => {
    console.warn('[setActiveThemeMode] Could not persist theme to backend:', error)
  })

  // 6. Internen Zustand updaten (für useTheme() und ThemeShowcase isSelected checks)
  currentThemeIdRef.value = themeId
  currentThemesRef.value = [...allThemesList]

  // 7. Window-Event für App.vue theme-ref Sync (TopBar Pill position, etc.)
  window.dispatchEvent(new CustomEvent('themeModeChanged', { detail: { mode } }))
}

/** Externer Modus-Setter — alias für backwards compat. */
export const setThemeMode = setActiveThemeMode

/** Dynamisch geladene Themes — nur als Fallback falls themes.json nicht geladen werden kann. */
export const allThemesRaw = FALLBACK_THEMES

/** Reaktiver Ref mit der aktuell aktiven Theme-ID (für Slider/TopBar).
 * Wird von TopBar.vue und ThemeShowcase.vue zum Synchronisieren des visuellen States genutzt. */
export const currentThemeIdRef: { value: string } = { value: FALLBACK_DEFAULT_THEME_ID }

/** Reaktiver Ref mit den aktuell aktiven Themes (Fallback vor init(), echte Werte nach init()).
 * Wird von TopBar.vue genutzt um die ThemeShowcase-Liste zu befüllen. */
export const currentThemesRef: { value: Theme[] } = { value: [...FALLBACK_THEMES] }

/** Lade Themes dynamisch mit eingebautem Fallback */
export async function loadThemes(): Promise<Theme[]> {
  if (isThemesLoaded && loadedThemesConfig) return loadedThemesConfig.themes
  await initThemes()
  return allThemesList
}

/** Gib den reaktiven Themes-Ref (für externe Komponenten die nicht useTheme nutzen können). */
export function getAllThemes(): Theme[] {
  return currentThemesRef.value
}

/**
 * Lade die gespeicherte Theme-ID aus dem Backend (mit localStorage-Fallback)
 * Gibt die Theme-ID für den aktuellen Modus zurück
 */
export async function getSavedThemeId(): Promise<string> {
  // Versuche zuerst vom Backend zu laden
  const backendSettings = await loadThemeFromBackend()

  if (backendSettings) {
    // Speichere Theme-IDs aus der Datenbank in localStorage
    if (isValidThemeId(backendSettings.colourSchemeLight)) {
      localStorage.setItem('selected-light-theme-id', backendSettings.colourSchemeLight)
    }
    if (isValidThemeId(backendSettings.colourSchemeDark)) {
      localStorage.setItem('selected-dark-theme-id', backendSettings.colourSchemeDark)
    }

    // Speichere font_size aus der Datenbank in localStorage
    if (backendSettings.fontSize >= 1) {
      localStorage.setItem('fontSize', String(backendSettings.fontSize))
    }

    // Bestimme die aktuelle Theme-ID basierend auf dem aktuellen Mode
    const currentMode = localStorage.getItem('theme') || 'light'
    const themeId =
      currentMode === 'dark' ? backendSettings.colourSchemeDark : backendSettings.colourSchemeLight

    if (isValidThemeId(themeId)) {
      localStorage.setItem('themeId', themeId)
      return themeId
    }
  }

  // Fallback: per-mode localStorage keys
  const darkModeId = localStorage.getItem('selected-dark-theme-id')
  const lightModeId = localStorage.getItem('selected-light-theme-id')

  if (darkModeId && isValidThemeId(darkModeId)) return darkModeId
  if (lightModeId && isValidThemeId(lightModeId)) return lightModeId

  // Legacy fallback
  const legacy = localStorage.getItem('themeId')
  if (legacy && isValidThemeId(legacy)) return legacy

  // Fallback: default aus themes.json
  return loadedThemesConfig?.defaultThemeId || FALLBACK_DEFAULT_THEME_ID
}
